import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {DataGridActionTypes, DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, DataGridExoticColumnFields, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import {TerritoryManagementDataGridColumnNames} from "../../../../../../../core/constants/table/data-grid-column-keys";
import Button from "../../../../../../components/base/button/button";
import {Add} from "@mui/icons-material";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import TerritoriesSearchSection from "../../../../../../components/search-sections/territories";
import UpsertTerritoryDialog from "../../../../../../components/dialogs/upsert-territory-dialog";
import useSearch from "../../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import Api from "../../../../../../../core/services/api/api";

const initUpsertDialogState = {open: false, data: null};

const exportingChunkSize = 50;

const dataGridSaveConfig = {
    name: 'TerritoryManagementViewDataGrid',
    version: 1,
}

/**
 * The available Actions for the territory management data grid.
 */
const TerritoryManagementDataGridActionNames = {
    Edit: 'edit',
    Delete: 'delete'
}

const TerritoryManagementView = () => {
    const [data, setData] = useState([]);
    const [upsertDialogState, setUpsertDialogState] = useState(initUpsertDialogState);
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<DataGridController>}*/
    const dataGridControllerRef = useRef();
    const {configureNotebookTags} = useConfigureNotebook('territories-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.territories);
        configureBehavioralTool((tags) => tags.territories);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    name: DataGridExoticColumnFields.selection,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    width: 100,
                    resizable: false,
                    visibilityToggleable: false,
                    reorderable: false,
                    sortable: false,
                },
                {
                    title: "Code",
                    name: TerritoryManagementDataGridColumnNames.code,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: "Name",
                    name: TerritoryManagementDataGridColumnNames.name,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 200,
                },
                {
                    title: "Description",
                    name: TerritoryManagementDataGridColumnNames.description,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: {type: DataGridColumnWidthTypes.flex, size: 1},
                },
            ],
        [])

    const [dataGridColumns, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
    }), [initialPaginationInfo]);

    /**
     * Gets the list of territories by calling the api regarding this action
     * If the function is being called for export, the pagination info is passed as the input
     * @param cancelTokenSource
     * @param forExport
     * @param _paginationInfo
     * @return {Promise<ApiResponseDs<*> | void>}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = {
            paginationInformation: args.paginationInfo,
            query: args.filters?.keyword,
        };
        const response = await Api.listTerritories(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.total ?? 0,
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, setData])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Exports the list of territories in the system by calling the search function with the pagination info
     * Calls the function for formatting the data
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const canExportTerritoriesList = operatorAccessLevels.packages.base.exportTerritoriesList;
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res.data.items);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        if (!entries.length)
            return;
        return {
            title: 'Territories Export',
            body: entries.map((e, i) => ({
                '': i + 1,
                'Code': e.code,
                'Name': e.name,
            }))
        }
    }, [fetchEntries, isMounted, searchData])

    /**
     * Opens the dialog for adding a new freight territory to the system
     */
    const onAddNewTerritoryClicked = () => {
        setUpsertDialogState({open: true, data: null});
    }

    /**
     * Data grid rows entities
     * @type {unknown}
     */
    const dataGridRows = useMemo(() => {
        return data?.map(record => ({
            key: record.id,
            data: record,
            cells: {
                [DataGridExoticColumnFields.selection]: null,
                [TerritoryManagementDataGridColumnNames.code]: record.code,
                [TerritoryManagementDataGridColumnNames.name]: record.name,
                [TerritoryManagementDataGridColumnNames.description]: record.description,
            }
        }))
    }, [data])

    /**
     * Memo version of data grid footer actions
     * @type {DataGridFooterAction[]}
     */
    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]}*/
        [
            {
                name: TerritoryManagementDataGridActionNames.Edit,
                title: 'Edit',
                visibilityConstraints: {
                    minSelectedRows: 1,
                    maxSelectedRows: 1,
                },
            },
            {
                name: TerritoryManagementDataGridActionNames.Delete,
                title: 'Delete',
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                },
            },
        ], [])

    /**
     * Opens dialog for editing the company information.
     * @param territory
     */
    const editSelectedTerritory = (territory) => {
        setUpsertDialogState({open: true, data: territory});
    }

    /**
     * Opens the confirmation dialog regarding this action
     * If the process was confirmed, calls the remove
     */
    const getRemoveConfirmation = (itemsToBeRemoved, allRecordsSelected) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {`Are you sure that you want to remove the selected ${itemsToBeRemoved?.length > 1 || allRecordsSelected ? 'territories' : 'territory'}?`}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeTerritories(itemsToBeRemoved),
        );
    }

    /**
     * Calls the api regarding removing the list of territory form the list
     * @param territoryIds list of ids associated to the territory to be removed
     * @private
     */
    const removeTerritories = async (territoryIds) => {
        searchData.setLoading(true);
        for (const id of territoryIds) {
            const response = await Api.deleteTerritory(id)
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                setData(prevState => prevState.filter(e => id !== e.id))
                searchData.setPaginationInfo(prevState => ({
                    ...prevState,
                    length: prevState.length - territoryIds.length
                }))
            }
        }
        dataGridControllerRef.current?.resetSelection();
        searchData.setLoading(false);
    }

    /**
     * Adds or updates the current list of territories based on the provided (upserted) territory.
     * @param territory
     */
    const onTerritoryUpserted = (territory) => {
        setData(prevState => {
            const foundIndex = prevState.findIndex(e => e.id === territory.id);
            if (foundIndex === -1) {
                return [territory, ...prevState];
            }
            return prevState.map(e => e.id === territory.id ? {...e, ...territory} : e)
        })
        dataGridControllerRef.current?.resetSelection();
    }

    /**
     * Fetches all the territory given the available length of the territory.
     * @param pageLength
     * @return {Promise<*[]>}
     */
    const getAllTerritories = async (pageLength) => {
        const _paginationInfo = {
            length: pageLength,
            pageSize: 20,
            currentPage: 1,
        };
        let entries = [];
        while (pageLength > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                continue;
            }
            if (!res?.data?.length) {
                break;
            }
            entries.push(...res?.data ?? []);
            if (!isMounted())
                return entries;
            _paginationInfo.currentPage++;
        }
        return entries;
    }

    /**
     * Handles the action over the selected row in the table based on the name of the action
     * @param action
     * @param selectedRows
     * @param excludedRows
     * @param allRowsSelected
     * @param pagination
     * @return {Promise<void>}
     */
    const onActionInvoked = async (action, selectedRows, excludedRows, allRowsSelected, pagination) => {
        switch (action.name) {
            case TerritoryManagementDataGridActionNames.Edit: {
                if (allRowsSelected && data.length === 1)
                    editSelectedTerritory(data[0]);
                else
                    editSelectedTerritory(selectedRows[0].data);
                break;
            }
            case TerritoryManagementDataGridActionNames.Delete: {
                if (!allRowsSelected) {
                    // not all rows are selected, therefore, the selectedRows list is not empty
                    return getRemoveConfirmation(selectedRows.map(e => e.data.id));
                }
                if (pagination.pageSize >= pagination.length) {
                    // all the items are within a single page, so we have the data in hand
                    return getRemoveConfirmation(data.map(e => e.id));
                }
                getRemoveConfirmation((await getAllTerritories(pagination.length)).map(e => e.id));
                break;
            }
            default:
                break;
        }
    }

    return (
        <>
            <div className={'freight-regions-list-view'}>
                <div className={'page-title-section'}>
                    <div/>
                    <Button
                        className={'d-flex align-items-center justify-content-center'}
                        variant={'outlined'}
                        color={'primary'}
                        onClick={onAddNewTerritoryClicked}
                    >
                        <Add className='icon small mr-2'/>
                        <p className={'button-label'}>Add a new Territory</p>
                    </Button>
                </div>
                <TerritoriesSearchSection searchData={searchData}/>
                <div className={'mb-5'}/>
                <PartnerDataGrid
                    ref={dataGridControllerRef}
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    footerActions={footerActions}
                    hideFooterActions={data?.length < 1}
                    onActionInvoked={onActionInvoked}
                    state={{
                        sortBy: searchData.orderBy,
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                        pagination: searchData.paginationInfo,
                    }}
                    getExportInfo={canExportTerritoriesList ? exportToCsv : undefined}
                    onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </div>
            <UpsertTerritoryDialog
                close={() => setUpsertDialogState(initUpsertDialogState)}
                open={upsertDialogState.open}
                data={upsertDialogState.data}
                onUpsert={onTerritoryUpserted}
            />
        </>
    )
}

export default TerritoryManagementView;
